import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, TextField, Button, MenuItem, Typography,
  Paper, Alert, CircularProgress
} from '@mui/material';
import { addCertificate } from '../apis/certificateService';
import './styling/AddCertificate.css';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);


// Month formatting
const monthFormat = "MMM-YY";
const dateFormat = "DD-MMM-YYYY";

const defaultState = {
  serialNumber: '',
  phone: '',
  name: '',
  fathersName: '',
  dob: '',
  registrationNumber: '',
  course: '',
  startMonth: '',
  endMonth: '',
  grade: '',
  issueDate: ''
};

const courses = ['TALLY', 'DCA', 'ADCA', 'DTP', 'PGDCA', 'BCC'];

const AddCertificate = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(defaultState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const {
      serialNumber, phone, name, fathersName, dob,
      registrationNumber, course, startMonth, endMonth,
      grade, issueDate
    } = form;

    if (!serialNumber || !phone || !name || !fathersName || !dob ||
        !registrationNumber || !course || !startMonth || !endMonth ||
        !grade || !issueDate) {
      return "All fields are mandatory";
    }

    const nameRegex = /^[A-Za-z ]+$/;
    if (!nameRegex.test(name)) return "Name should contain only letters & spaces";
    if (!nameRegex.test(fathersName)) return "Father's Name should contain only letters & spaces";

    if (!/^\d+$/.test(serialNumber)) return "Serial Number must be numeric";
    if (!/^\d+$/.test(phone)) return "Phone Number must be numeric";
    if (phone.length !== 10) return "Phone Number must be 10 digits";

    if (!/^[A-Z]$/.test(grade)) return "Grade must be a single uppercase letter";

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      await addCertificate(form);
      setSuccess("Certificate added successfully!");
      setForm(defaultState);
      setTimeout(() => navigate("/admin/dashboard"), 900);
    } catch (err) {
      setError(err.message || "Failed to add certificate");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-cert-bg">
      <Paper className="add-cert-card" elevation={4}>
        
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Add New Certificate
        </Typography>
        <Typography sx={{ mb: 3, opacity: 0.8 }}>
          Fill the form below to add a student's certificate details.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box component="form" onSubmit={handleSubmit} className="add-cert-form">

            <Box className="row">
              <TextField label="Serial Number" name="serialNumber" value={form.serialNumber} onChange={handleChange} />
              <TextField label="Registration No" name="registrationNumber" value={form.registrationNumber} onChange={handleChange} />
            </Box>

            <Box className="row">
              <TextField fullWidth label="Student Name" name="name" value={form.name} onChange={handleChange} />
            </Box>

            <Box className="row">
              <TextField fullWidth label="Father's Name" name="fathersName" value={form.fathersName} onChange={handleChange} />
            </Box>

            <Box className="row">
              <TextField label="Phone Number" name="phone" value={form.phone} onChange={handleChange} />

              <DatePicker
                label="DOB"
                value={form.dob ? dayjs(form.dob, dateFormat) : null}
                onChange={(val) =>
                  setForm(prev => ({ ...prev, dob: val ? dayjs(val).format(dateFormat) : "" }))
                }
              />
            </Box>

            <Box className="row">
              <TextField select label="Course" name="course" value={form.course} onChange={handleChange}>
                {courses.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>

              <TextField label="Grade" name="grade" value={form.grade} onChange={handleChange} />
            </Box>

            <Box className="row">
              <DatePicker
                views={['month', 'year']}
                label="Start Month"
                value={form.startMonth ? dayjs(form.startMonth, monthFormat) : null}
                onChange={(val) =>
                  setForm(prev => ({ ...prev, startMonth: val ? dayjs(val).format(monthFormat) : "" }))
                }
              />

              <DatePicker
                views={['month', 'year']}
                label="End Month"
                value={form.endMonth ? dayjs(form.endMonth, monthFormat) : null}
                onChange={(val) =>
                  setForm(prev => ({ ...prev, endMonth: val ? dayjs(val).format(monthFormat) : "" }))
                }
              />
            </Box>

            <Box className="row">
              <DatePicker
                label="Issue Date"
                value={form.issueDate ? dayjs(form.issueDate, dateFormat) : null}
                onChange={(val) =>
                  setForm(prev => ({ ...prev, issueDate: val ? dayjs(val).format(dateFormat) : "" }))
                }
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button type="submit" variant="contained" className="submit-btn" disabled={loading}>
                {loading ? <CircularProgress size={18} /> : "Submit"}
              </Button>
              <Button variant="outlined" onClick={() => navigate('/admin/dashboard')}>
                Cancel
              </Button>
            </Box>

          </Box>
        </LocalizationProvider>
      </Paper>
    </div>
  );
};

export default AddCertificate;
